import { Fragment, useId, useMemo, useState } from 'react';
import { useEffectOnce } from 'react-use';
import { playSound, Sound } from '~/assets/sounds';
import { Divider } from '~/components/ui/Divider';
import { ImgIcon } from '~/components/ui/ImgIcon';
import { RadioButton } from '~/components/ui/RadioButton';
import { Text } from '~/components/ui/Text';
import { useWalletIcon } from '~/hooks';
import { rage, useInputAction } from '~/rage';
import { Currency, IModalStatePrice, InputAction, WalletType } from '~/shared';
import { CurrencyIcon } from '../../CurrencyIcon';
import { ModalButton } from '../ModalButton';
import {
	StyledButtonsContainer,
	StyledFieldValue,
	StyledModalBody,
	StyledPrice,
	StyledPriceContainer,
	StyledRadioContainer,
} from './ModalPrice.styled';

const ModalPrice: React.FC<{ state: IModalStatePrice }> = ({ state }) => {
	const id = useId();
	const [selectedWalletType, setSelectedWalletType] =
		useState<WalletType | null>(null);

	const rubleIcon = useWalletIcon(Currency.Ruble, true);
	const donateIcon = useWalletIcon(Currency.Donate, true);

	const isDonate = useMemo(
		() => (selectedWalletType || state?.wallet) === WalletType.Donate,
		[selectedWalletType, state?.wallet],
	);

	useInputAction(InputAction.CommonMenuAccept, () => {
		rage.modal.replyToCurrentModal({
			isAccepted: true,
			value: selectedWalletType,
		});
	});

	useInputAction(InputAction.CommonMenuClose, () => {
		rage.modal.replyToCurrentModal({
			isAccepted: false,
		});
	});

	useEffectOnce(() => {
		if (!state?.walletTypes) {
			return;
		}

		if (state?.walletTypes?.length > 0) {
			setSelectedWalletType(state.walletTypes[0]);
		}
	});

	return (
		<StyledModalBody>
			<Text style={{ fontSize: 16, color: '#ffffff', opacity: 0.75 }}>
				{state?.text}
			</Text>
			<Divider $direction={'H'} style={{ opacity: 0.1 }} />
			{state?.walletTypes?.length && (
				<Fragment>
					<StyledPriceContainer>
						<Text style={{ fontSize: 18, color: '#ffffff', opacity: 0.25 }}>
							Способ оплаты
						</Text>
						<StyledRadioContainer>
							{state?.walletTypes.map((item) => (
								<RadioButton
									name={id}
									id={item}
									key={item}
									onChange={(e) =>
										setSelectedWalletType(e.currentTarget.id as WalletType)
									}
									defaultChecked={item === state?.walletTypes?.[0]}
								>
									{item === WalletType.Donate
										? 'CCD Coin'
										: item === WalletType.Cash
										? 'Наличными'
										: 'Банк'}
								</RadioButton>
							))}
						</StyledRadioContainer>
					</StyledPriceContainer>
					<Divider $direction={'H'} style={{ opacity: 0.1 }} />
				</Fragment>
			)}
			{state?.fields?.map((field, index) => (
				<Fragment key={`${field.label}-${index}`}>
					<StyledPriceContainer>
						<Text style={{ fontSize: 18, color: '#ffffff', opacity: 0.25 }}>
							{field.label}
						</Text>
						<StyledFieldValue $color={field.valueColor}>
							<Text>
								{field.value}
								{field.valueSuffix && <span>{field.valueSuffix}</span>}
							</Text>
							{field.icon && (
								<ImgIcon
									icon={field.icon}
									width={16}
									height={16}
									color={field.iconColor ?? '#ffbf42'}
								/>
							)}
						</StyledFieldValue>
					</StyledPriceContainer>
					<Divider $direction={'H'} style={{ opacity: 0.1 }} />
				</Fragment>
			))}
			<StyledPriceContainer>
				<Text style={{ fontSize: 18, color: '#ffffff', opacity: 0.25 }}>
					Стоимость
				</Text>
				<StyledPrice>
					<Text>
						{selectedWalletType === WalletType.Donate
							? state.priceDonate?.toLocaleString('ru-RU')
							: state?.price?.toLocaleString('ru-RU')}
					</Text>
					<CurrencyIcon
						icon={isDonate ? donateIcon : rubleIcon}
						size={'md'}
						style={{ opacity: isDonate ? 1 : 0.5, marginTop: -2 }}
					/>
				</StyledPrice>
			</StyledPriceContainer>
			<StyledButtonsContainer>
				<ModalButton
					$type={state?.cancelButton.color}
					onClick={() => {
						rage.modal.replyToCurrentModal({ isAccepted: false });
						playSound(Sound.ClickButton);
					}}
					onMouseEnter={() => playSound(Sound.LinkCursor)}
				>
					{state?.cancelButton.text}
				</ModalButton>
				<ModalButton
					$type={state?.acceptButton.color}
					onClick={() => {
						rage.modal.replyToCurrentModal({
							isAccepted: true,
							value: selectedWalletType,
						});
						playSound(Sound.ClickButton);
					}}
					onMouseEnter={() => playSound(Sound.LinkCursor)}
				>
					{state?.acceptButton.text}
				</ModalButton>
			</StyledButtonsContainer>
		</StyledModalBody>
	);
};

export { ModalPrice };
